"use client"

import { format } from "date-fns"
import { useState } from "react"
import { CirclesWithBar } from "react-loader-spinner"
import Swal from "sweetalert2"
import { replyToMessage } from "../../api/support"

export function SupportMessageList({ messages, loading, onRefresh }) {
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [replyText, setReplyText] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleReply = async (e) => {
    e.preventDefault()
    if (!replyText.trim()) return

    setIsSubmitting(true)
    try {
      const response = await replyToMessage(selectedMessage._id, replyText)
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.message || "Reply sent successfully",
        })
        setSelectedMessage(null)
        setReplyText("")
        if (onRefresh) onRefresh()
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to send reply",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center p-5">
        <CirclesWithBar
          height="100"
          width="100"
          color="#05468f"
          outerCircleColor="#05468f"
          innerCircleColor="#05468f"
          barColor="#05468f"
          ariaLabel="circles-with-bar-loading"
          visible={true}
        />
      </div>
    )
  }

  if (!messages || messages.length === 0) {
    return <div className="alert alert-info">No support messages found.</div>
  }

  return (
    <>
      <div className="table-responsive">
        <table className="table table-hover table-center mb-0">
          <thead>
            <tr>
              <th>Date</th>
              <th>Name</th>
              <th>Email</th>
              <th>Subject</th>
              <th>Status</th>
              <th className="text-end">Action</th>
            </tr>
          </thead>
          <tbody>
            {messages.map((msg) => (
              <tr key={msg._id}>
                <td>{format(new Date(msg.createdAt), "dd MMM yyyy, HH:mm")}</td>
                <td>{msg.fullName}</td>
                <td>{msg.email}</td>
                <td>{msg.subject}</td>
                <td>
                  <span className={`badge rounded-pill ${msg.status === "New" ? "bg-success" : "bg-info"}`}>
                    {msg.status}
                  </span>
                </td>
                <td className="text-end">
                  {msg.status === "New" ? (
                    <button className="btn btn-sm btn-outline-primary me-2" onClick={() => setSelectedMessage(msg)}>
                      <i className="fa fa-reply"></i> Reply
                    </button>
                  ) : (
                    <button className="btn btn-sm btn-outline-secondary me-2" disabled>
                      <i className="fa fa-check"></i> Replied
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedMessage && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reply to Message</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setSelectedMessage(null)
                    setReplyText("")
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 border-end">
                    <h6>Original Message</h6>
                    <p className="mb-1">
                      <strong>From:</strong> {selectedMessage.fullName}
                    </p>
                    <p className="mb-1">
                      <strong>Email:</strong> {selectedMessage.email}
                    </p>
                    <p className="mb-1">
                      <strong>Subject:</strong> {selectedMessage.subject}
                    </p>
                    <p className="mb-3">
                      <strong>Date:</strong> {format(new Date(selectedMessage.createdAt), "dd MMM yyyy, HH:mm")}
                    </p>
                    <div className="p-3 bg-light rounded" style={{ maxHeight: "200px", overflowY: "auto" }}>
                      {selectedMessage.message}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h6>Send Reply</h6>
                    <form onSubmit={handleReply}>
                      <div className="mb-3">
                        <textarea
                          className="form-control"
                          rows="8"
                          placeholder="Type your reply here..."
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          required
                          disabled={isSubmitting}
                        ></textarea>
                      </div>
                      <div className="text-end">
                        <button type="submit" className="btn btn-primary" disabled={isSubmitting || !replyText.trim()}>
                          {isSubmitting ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Sending...
                            </>
                          ) : (
                            "Send Reply"
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
